import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useState, useEffect, createContext, useContext, useRef } from "react";

const TooltipManagerContext = createContext({
  openTooltipId: null,
  setOpenTooltipId: () => {},
});

export const TooltipManagerProvider = ({ children }) => {
  const [openTooltipId, setOpenTooltipId] = useState(null);

  return (
    <TooltipManagerContext.Provider value={{ openTooltipId, setOpenTooltipId }}>
      {children}
    </TooltipManagerContext.Provider>
  );
};

export const TooltipProvider = ({
  children,
  delayDuration = 300,
  skipDelayDuration = 300,
  disableHoverableContent = false,
}) => {
  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      {children}
    </TooltipPrimitive.Provider>
  );
};

export const Tooltip = ({
  children,
  content,
  title,
  side = "top",
  align = "center",
  sideOffset = 5,
  alignOffset = 0,
  maxWidth = "300px",
  className,
  contentClassName,
  delayDuration,
  disableHoverableContent,
  open,
  defaultOpen,
  onOpenChange,
  arrowClassName,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const tooltipIdRef = useRef(Math.random().toString(36).substr(2, 9));
  const { openTooltipId, setOpenTooltipId } = useContext(TooltipManagerContext);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.matchMedia("(max-width: 1024px)").matches ||
          "ontouchstart" in window
      );
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (
      isMobile &&
      openTooltipId !== null &&
      openTooltipId !== tooltipIdRef.current
    ) {
      setIsOpen(false);
    }
  }, [openTooltipId, isMobile]);

  useEffect(() => {
    if (isMobile && isOpen) {
      const handleClickOutside = (e) => {
        setIsOpen(false);
        setOpenTooltipId(null);
      };

      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [isMobile, isOpen, setOpenTooltipId]);

  if (!content && !title) {
    return children;
  }

  const handleClick = (e) => {
    if (isMobile) {
      e.stopPropagation();
      const newState = !isOpen;
      setIsOpen(newState);

      if (newState) {
        setOpenTooltipId(tooltipIdRef.current);
      } else {
        setOpenTooltipId(null);
      }
    }
  };

  return (
    <TooltipPrimitive.Root
      delayDuration={delayDuration}
      disableHoverableContent={isMobile ? true : disableHoverableContent}
      open={isMobile ? isOpen : open}
      defaultOpen={defaultOpen}
      onOpenChange={isMobile ? setIsOpen : onOpenChange}
    >
      <TooltipPrimitive.Trigger
        asChild
        className={className}
        onClick={handleClick}
      >
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className={`z-[99999999999] shadow-lg rounded-[6px] bg-primary-off-white px-[10px] py-[8px] font-normal text-primary-black ${
            contentClassName || "text-[12px]"
          }`}
          style={{
            maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
          onPointerDownOutside={(e) => {
            if (isMobile) {
              e.preventDefault();
            }
          }}
        >
          <div className="flex flex-col gap-[4px]">
            {title && (
              <p className="text-[14px] font-bold text-primary-black">
                {`${title}`}
              </p>
            )}
            {content && (
              <p className="text-[12px] text-primary-black">{content}</p>
            )}
          </div>
          <TooltipPrimitive.Arrow
            className={arrowClassName || "fill-primary-off-white"}
            width={16}
            height={8}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};
